// src/js/cindy/live-timer.ts
//
// Phase 264 (CINDY-01/CINDY-03/CINDY-04) — the DOM-owning module for /workouts/live. This file
// owns listeners, rendering and browser APIs and NOTHING ELSE: every piece of clock math, every
// state change and every persistence decision goes through ./live-timer-state's pure
// functions (264-01/264-04) — never reimplemented here.
//
// Plan 264-06 landed: the mount, the mount-time resume/discard routing decision, the localStorage
// feature-detect, and the pre-start Start button / P-14 Resume+Discard button wiring.
//
// Plan 264-07 lands the running state's own continuous behaviour on top of that same closure: the
// wall-clock repaint loop (`render()`/`startRenderLoop()`/`stopRenderLoop()`, Task 1), the
// `visibilitychange` + `pageshow` reconciliation listeners (P-09, `recomputeAndRepaint()`, Task 2),
// and a best-effort Wake Lock request/release/re-acquire cycle (Task 3).
//
// Plan 264-08 lands the operator-facing capture payload on top of that same closure: the single
// tap-target click handler in both 1-tap and 3-tap modes plus its debounce lockout (Task 1), the
// live round-history list rendered every `render()` pass (Task 2), and the Undo affordance
// (Task 3) — all three consume `./live-timer-state`'s `captureTap()`/`undoLast()`/
// `describeTapTarget()`/`describeUndoTarget()` pure functions rather than reimplementing any
// capture/cycle/removal logic here.
//
// Plan 264-09 (this plan) closes the loop on that same closure: cap detection evaluated inside
// `render()` itself (Task 1 — so a cap discovered on a post-visibilitychange recompute fires
// exactly like an ordinary tick), a best-effort vibration pulse plus the inline cap/manual finish
// panel (`showFinishPanel()`/`hideFinishPanel()`, Task 1/Task 2), and the P-02 hand-off
// (`enterReviewState()`, Task 3) that calls `.refresh()` on the ALREADY-MOUNTED
// `opts.entryController` — never a second entry-form mount, never a new save path. All these
// plans extend this SAME file/closure rather than opening a second DOM-owning module for the
// page — see `render()`, `currentState` and `teardownListeners` below, which are the exact seams
// those plans hook into.
//
// T-264-02 mitigation: this file assigns ZERO raw-HTML-string markup for any interpolated/
// dynamic value. The four [data-live-state] containers are static server-rendered markup
// (live.latte, Plan 264-02) — this module only ever toggles their `d-none` class and assigns
// `.textContent` on the slots inside them (`clearChildren()` below is the escape hatch a future
// plan needs the moment it renders a LIST of dynamic rows, e.g. Plan 264-08's round-history list).

import { CindyEntryController } from './entry-form';
import {
    LiveTimerState,
    StorageLike,
    STORAGE_KEY,
    loadPersistedState,
    persistState,
    clearPersistedState,
    isStorageAvailable,
    createInitialState,
    startTimer,
    isResumable,
    isCapReached,
    markCapReached,
    finishSession,
    computeElapsed,
    computeRemaining,
    formatClock,
    formatDurationDisplay,
    describeTapTarget,
    describeUndoTarget,
    formatResumeDate,
    captureTap,
    undoLast,
    toEntrySession,
    toLocalYmd,
    MOVEMENT_ORDER,
    MOVEMENT_LABELS,
} from './live-timer-state';

type LiveState = 'pre-start' | 'resume' | 'running' | 'review';

/** Empties an element's children without a raw-HTML-string assignment (T-263-16 / T-264-02). A
 *  third module-private copy — matching this codebase's existing precedent (entry-form.ts,
 *  round-entry.ts) of NOT sharing this one helper across modules. First consumer:
 *  `renderRoundHistory()` below (Plan 264-08 Task 2), which empties `.cindy-live-round-list`
 *  before rebuilding it on every `render()` pass. */
function clearChildren(el: HTMLElement): void {
    while (el.firstChild) {
        el.removeChild(el.firstChild);
    }
}

/** In-memory fallback satisfying live-timer-state.ts's StorageLike shape. Used only when the real
 *  localStorage is unavailable (Safari private browsing, quota-disabled, or the PROPERTY ACCESS
 *  itself throwing) — keeps every later code path in this file identical whether persistence is
 *  real or not, so nothing downstream ever has to branch on it (T-264-05). */
function createMemoryStorage(): StorageLike {
    const memory = new Map<string, string>();
    return {
        getItem: (key: string): string | null => (memory.has(key) ? (memory.get(key) as string) : null),
        setItem: (key: string, value: string): void => {
            memory.set(key, value);
        },
        removeItem: (key: string): void => {
            memory.delete(key);
        },
    };
}

/**
 * Resolves the storage this session will use, feature-detecting exactly once at mount. Wraps the
 * `window.localStorage` PROPERTY ACCESS itself in try/catch — Safari private browsing can throw
 * on the access, not only on a subsequent read/write call — then confirms the result is actually
 * usable via `isStorageAvailable()`'s own write/read/delete round trip. On any failure, unhides
 * `warningEl` (UI-SPEC's exact copy already ships in live.latte, unmodified by this file) and
 * falls back to the in-memory shim above. The warning is shown ONLY on a failed feature-detect —
 * never speculatively — and never blocks starting the timer.
 */
function resolveStorage(warningEl: HTMLElement | null): StorageLike {
    let real: StorageLike | null;
    try {
        real = window.localStorage;
    } catch {
        real = null;
    }
    if (real !== null && isStorageAvailable(real)) {
        return real;
    }
    if (warningEl) {
        warningEl.classList.remove('d-none');
    }
    return createMemoryStorage();
}

/**
 * Best-effort vibration pulse at the cap (P-13, RESEARCH.md Pattern 4). The feature-detect is
 * genuinely two-part — `'vibrate' in navigator` catches an absent API outright, the `typeof`
 * check on the extracted reference below additionally catches a spec-compliant "supported but
 * silently does nothing" implementation — without any user-agent sniffing, which this codebase
 * does not use anywhere for a capability decision. The call itself is wrapped in try/catch: some
 * browsers throw for a pattern exceeding a length/duration cap. Nothing here may ever branch on
 * whether the pulse actually fired — no copy, no icon, no console output, no return-value read,
 * no retry (P-13 — iOS Safari has no Vibration support at all, so the visible countdown and the
 * correct capped state on the next wake are what the operator actually relies on).
 */
function vibrateBestEffort(pattern: number[]): void {
    const vibrate = 'vibrate' in navigator ? navigator.vibrate : undefined;
    if (typeof vibrate !== 'function') return;
    try {
        vibrate.call(navigator, pattern);
    } catch {
        // Some browsers throw for an over-length pattern — never surfaced to the operator.
    }
}

/**
 * Mounts the live timer onto its static server-rendered shell. `rootEl` is `#cindy-live-timer`
 * (guarded by the caller, src/js/workouts.ts's own `liveTimer()` — this function assumes it
 * already exists). `opts.entryController` is the already-mounted P-02 review-form controller
 * (`live.latte`'s hidden `review` container ships `[data-cindy-entry]`, mounted at page load by
 * the existing `entryForm()` in workouts.ts) — `enterReviewState()` below is its one consumer,
 * repopulating it in place at finish time rather than mounting a second one.
 */
export function mountLiveTimer(rootEl: HTMLElement, opts: { entryController: CindyEntryController | null }): void {
    // ---- data-* mirrored constant — the single-source-of-truth idiom console.ts's
    // dataset.streamUrl and workouts.ts's dataset.session already establish (264-PATTERNS.md). The
    // fallback literal below documents its PHP source of truth (CindyModel::CAP_SECONDS) so it
    // never silently drifts into a second, independent hardcoded number.
    //
    // IN-02: the pullupsPerRound/pushupsPerRound/squatsPerRound dataset reads this block used to
    // carry (CindyModel::PULLUPS_PER_ROUND/PUSHUPS_PER_ROUND/SQUATS_PER_ROUND) were removed —
    // Cindy's prescribed scheme reminder is static server copy in live.latte's pre-start
    // container, and no consumer of those three variables ever materialized. Re-add the dataset
    // reads (still emitted by live.latte, unaffected by this removal) the moment an actual
    // consumer needs them, rather than carrying unused locals forward speculatively.
    //
    // IN-01: falls back to 1200 not only when the attribute is absent (the `??` below already
    // covers that) but also when it is present-but-malformed (empty string, non-numeric) — a
    // future template regression would otherwise silently produce NaN, and isCapReached() reading
    // `elapsed >= NaN` is always false, meaning the AMRAP would simply never cap.
    const parsedCapSeconds = parseInt(rootEl.dataset.capSeconds ?? '1200', 10); // 1200 documents CindyModel::CAP_SECONDS
    const capSeconds = Number.isFinite(parsedCapSeconds) && parsedCapSeconds > 0 ? parsedCapSeconds : 1200;

    // ---- DOM anchors — every one of these is static markup live.latte (Plan 264-02) already
    // ships; this module never creates, replaces or reorders any of the four state containers. ----
    const containers = Array.from(rootEl.querySelectorAll<HTMLElement>('[data-live-state]'));
    const warningEl = rootEl.querySelector<HTMLElement>('.cindy-storage-warning');
    const splitsToggle = rootEl.querySelector<HTMLInputElement>('#cindy-splits-toggle');
    const startBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-start-btn');
    const resumeDateEl = rootEl.querySelector<HTMLElement>('.cindy-resume-date');
    const resumeBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-resume-btn');
    const discardBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-discard-btn');
    const remainingEl = rootEl.querySelector<HTMLElement>('.cindy-timer-remaining');
    const elapsedEl = rootEl.querySelector<HTMLElement>('.cindy-timer-elapsed');
    const tapBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-tap-target');
    const tapLabelEl = rootEl.querySelector<HTMLElement>('.cindy-tap-target__label');
    const tapRoundEl = rootEl.querySelector<HTMLElement>('.cindy-tap-target__round');
    const undoBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-undo-btn');
    const roundListEl = rootEl.querySelector<HTMLElement>('.cindy-live-round-list');
    const finishPanelEl = rootEl.querySelector<HTMLElement>('.cindy-finish-panel');
    const finishPanelTitleEl = rootEl.querySelector<HTMLElement>('.cindy-finish-panel-title');
    const continueBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-continue-btn');
    const finishEarlyBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-finish-early-btn');
    const finishBtn = rootEl.querySelector<HTMLButtonElement>('.cindy-finish-btn');
    const leftoverInput = rootEl.querySelector<HTMLInputElement>('#cindy-leftover-reps');
    const tabConflictWarningEl = rootEl.querySelector<HTMLElement>('.cindy-tab-conflict-warning'); // WR-03

    /** Toggles `d-none` across the four static `[data-live-state]` containers — exactly one
     *  visible at a time. The ONLY way this module ever changes which screen is showing; it never
     *  creates/removes/reorders a container. */
    function showState(name: LiveState): void {
        for (const container of containers) {
            container.classList.toggle('d-none', container.getAttribute('data-live-state') !== name);
        }
    }

    const storage = resolveStorage(warningEl);

    // ---- Session state — every later plan (264-07 through 264-09) reads/writes this SAME
    // closure-scoped variable rather than re-deriving state from the DOM on every interaction; it
    // is the one place this module keeps the currently-active LiveTimerState. ----
    let currentState: LiveTimerState = createInitialState(false);

    // ---- WR-03 cross-tab writer lock. Flips to `true` exactly once, by `lockTabAsSuperseded()`
    // below, the moment a foreign write to STORAGE_KEY is observed while this tab's own session is
    // still live. Every persistState() call in this closure funnels through persistIfActive()
    // rather than calling the imported function directly, so this is the ONE flag that decides
    // whether this tab is still allowed to write. ----
    let isSupersededTab = false;

    // ---- Repaint loop bookkeeping (264-07 Task 1). `renderIntervalId` is the ONLY repeating timer
    // this module ever schedules — its sole job is to call `render()` on a fixed cadence; it is
    // never itself a source of truth for elapsed time. ----
    let renderIntervalId: ReturnType<typeof setInterval> | null = null;

    // ---- Tap debounce-lockout bookkeeping (Task 1 below). A single one-shot timeout id — never a
    // second repeating timer — that re-enables `tapBtn` after the lockout window. Cleared in
    // `teardownListeners()` so a navigation mid-lockout can never fire a stray re-enable. ----
    let tapLockoutId: ReturnType<typeof setTimeout> | null = null;

    // ---- Wake Lock bookkeeping (Task 3, best-effort only — P-10, REQUIREMENTS.md's Wake Lock
    // exclusion from the correctness contract). Named `sentinel` rather than anything containing
    // the API's own property name, so the five literal call sites below (feature-detect, request,
    // release listener, re-request, teardown release) are the only places that name it. ----
    let sentinel: WakeLockSentinel | null = null;

    /**
     * Paints the tap target's label/round sub-label and the Undo button's label/disabled state
     * from `currentState` — extracted (WR-01) so the cap branch below can call it a SECOND time,
     * immediately after `markCapReached()` mutates state, and not only once before. Painting only
     * once, before that mutation, could freeze a stale label (e.g. "Undo Squats" for a
     * mid-round split that `markCapReached()` just discarded) on screen indefinitely — the same
     * `render()` call that would have corrected it also calls `stopRenderLoop()`.
     */
    function paintTapAndUndoLabels(): void {
        const tapTarget = describeTapTarget(currentState);
        if (tapLabelEl) tapLabelEl.textContent = tapTarget.label;
        if (tapRoundEl) tapRoundEl.textContent = tapTarget.roundLabel;

        const undoTarget = describeUndoTarget(currentState);
        if (undoBtn) {
            undoBtn.textContent = undoTarget.label;
            // WR-01: describeUndoTarget() only knows whether there is anything left TO undo — it
            // has no notion of session status. onUndoClick() itself no-ops for any status besides
            // 'running', so the PAINTED disabled state must independently enforce that same
            // invariant. Without the `|| status !== 'running'` half of this, a `pageshow` firing
            // after the cap branch already ran (a NORMAL browser event on every navigation, not
            // only a background/foreground cycle) would call this function again and silently
            // re-enable Undo — the cap branch's own idempotency guard (finishPanelEl already
            // visible) means it never gets a second chance to re-disable it.
            undoBtn.disabled = undoTarget.disabled || currentState.status !== 'running';
        }
    }

    /**
     * CR-01/WR-01: applies the capped-screen UI — disables+hides `tapBtn`, disables `undoBtn` and
     * `finishEarlyBtn` (WR-01 — mirroring `tapBtn`, since both are otherwise silent no-ops once
     * capped but stayed visually live), reveals the cap finish panel — and NOTHING else: never
     * mutates `currentState`, never persists, never vibrates. Idempotent by construction (every
     * line here is a plain assignment, safe to run any number of times), which is what lets it be
     * called from TWO call sites without those two ever drifting apart: `render()`'s own cap
     * branch below (the first time a live session's `status` moves past `'running'`) AND
     * mount-time routing (a page load/reload landing directly on an ALREADY-`'cap-reached'`
     * persisted session, which has nothing left to resume from a wall-clock perspective). Before
     * this helper existed, only the first call site applied this UI at all — a fresh mount into an
     * already-capped session left `tapBtn` in its live-looking template default with no way to
     * reach the finish panel.
     */
    function applyCapUi(): void {
        if (tapBtn) {
            tapBtn.disabled = true;
            tapBtn.classList.add('d-none');
        }
        if (undoBtn) undoBtn.disabled = true;
        if (finishEarlyBtn) finishEarlyBtn.disabled = true;
        showFinishPanel('cap');
    }

    /**
     * The single place anything is painted while the timer is running — the interval tick and the
     * Start/Resume one-time paint (below) both funnel through this exact function and nothing
     * else. PITFALLS.md Pitfall 3's warning signs are (1) a displayed value that is a variable
     * incremented inside an interval callback, and (2) no persisted start instant to reconcile
     * against on resume — this function makes both structurally impossible: it reads `Date.now()`
     * exactly once per call, derives every value from `computeElapsed()`/`computeRemaining()`
     * against the persisted `currentState.startInstant`, and never reads or increments any stored
     * elapsed/remaining field. The browser's monotonic high-resolution timer (PITFALLS.md
     * Pitfall 3's named unsafe API for this exact use case) must NEVER be used here or anywhere in
     * this module — it is not guaranteed to keep advancing while the OS suspends a
     * backgrounded/locked page, which is the exact failure this phase exists to prevent.
     */
    function render(): void {
        const now = Date.now();
        const rawElapsedSeconds =
            currentState.startInstant !== null ? computeElapsed(currentState.startInstant, now) : 0;
        const capped = isCapReached(rawElapsedSeconds, capSeconds);
        const elapsedSeconds = capped ? capSeconds : rawElapsedSeconds;
        const remainingSeconds = computeRemaining(elapsedSeconds, capSeconds);

        if (remainingEl) remainingEl.textContent = formatClock(remainingSeconds);
        if (elapsedEl) elapsedEl.textContent = formatClock(elapsedSeconds);

        paintTapAndUndoLabels();
        renderRoundHistory();

        // P-12 cap handling: evaluated on EVERY render() call (isCapReached() above, not gated
        // behind the interval tick alone) so a cap discovered on the recompute that follows a
        // visibilitychange/pageshow after a long screen-lock fires exactly like an ordinary tick
        // would. Guarding the FIRST inner branch on `status === 'running'` (not on `capped`
        // alone) is what keeps the state mutation/persist/vibrate side idempotent: three forced
        // consecutive render() calls after the cap mutate state and vibrate exactly once, since
        // every render() after the first already observes `status === 'cap-reached'`. CR-01: the
        // SECOND inner branch (added on top of the original single branch) is what makes the UI
        // side idempotent too, ACROSS PAGE LOADS — it re-applies applyCapUi() whenever `render()`
        // observes an already-`'cap-reached'` status with the finish panel not yet showing,
        // which is exactly mount-time routing's own situation below, so a fresh mount/Resume into
        // an already-capped session never has to duplicate this UI-application logic itself.
        if (capped) {
            if (currentState.status === 'running') {
                currentState = markCapReached(currentState);
                persistIfActive(currentState); // WR-03: no-op once this tab is superseded (see persistIfActive() above)
                // WR-01: repaint AFTER the mutation above, which discards any mid-round pending
                // split — the paint at the top of this function already ran against the
                // PRE-mutation state, and could be showing a label (e.g. "Undo Squats") for a
                // split that no longer exists. stopRenderLoop() below ends the interval on this
                // exact call, so this is the only chance to correct it.
                paintTapAndUndoLabels();
                applyCapUi();
                vibrateBestEffort([200, 100, 200]);
            } else if (currentState.status === 'cap-reached' && finishPanelEl?.classList.contains('d-none')) {
                applyCapUi();
            }
            stopRenderLoop();
        }
    }

    /**
     * Repaints `.cindy-live-round-list` from `currentState.capturedRounds` — P-04's visible
     * confirmation surface that a tap landed, and D-12's opt-in splits sub-line surface. Called
     * from EVERY `render()` pass (interval tick, one-time Start/Resume paint, tap repaint, both
     * reconciliation triggers) rather than being its own separate paint path — this module has
     * exactly one place anything is painted (`render()`'s own docblock above).
     *
     * Newest-first (the just-completed round is the first child) is a deliberate divergence from
     * `round-entry.ts`'s forward-numbered entry-form list: this is a live glance-feed the operator
     * checks to confirm "did my last tap register," not an edit surface, and `toEntrySession()`'s
     * ascending `round_no` hand-off payload (`live-timer-state.ts`) is entirely unaffected by this
     * display-only ordering choice.
     *
     * Built with `document.createElement` and `.textContent`/`classList` only (T-264-02) — every
     * value here originates in an operator-editable `localStorage` blob. No per-row delete control
     * and no empty-state message, per 264-UI-SPEC.md's Round-History List contract: the single
     * global Undo button above is the only correction affordance (P-08), and an empty list on a
     * freshly-started clock needs no placeholder copy.
     */
    function renderRoundHistory(): void {
        if (!roundListEl) return;
        clearChildren(roundListEl);

        const roundsNewestFirst = currentState.capturedRounds.slice().reverse();
        for (const round of roundsNewestFirst) {
            const row = document.createElement('div');
            row.className = 'd-flex align-items-center gap-2 mb-2 cindy-round-row';

            const badge = document.createElement('span');
            badge.className = 'badge text-bg-secondary cindy-round-badge';
            badge.textContent = 'R' + round.roundNo;
            row.appendChild(badge);

            const duration = document.createElement('span');
            duration.className = 'cindy-round-duration-readonly';
            duration.textContent = formatDurationDisplay(round.durationSeconds);
            row.appendChild(duration);

            roundListEl.appendChild(row);

            // Splits sub-line renders ONLY when this round has splits (3-tap mode) — no empty
            // placeholder line in 1-tap mode.
            if (round.splits) {
                const splits = round.splits;
                const splitLine = document.createElement('div');
                splitLine.className = 'cindy-round-split-line small text-muted';
                MOVEMENT_ORDER.forEach((movementKey, idx) => {
                    const dot = document.createElement('span');
                    dot.className = 'cindy-split-dot';
                    dot.classList.add('cindy-split-dot--' + movementKey); // colour lives in the stylesheet (264-06), never an inline style
                    splitLine.appendChild(dot);

                    const seconds = Math.round(splits[movementKey]);
                    const separator = idx < MOVEMENT_ORDER.length - 1 ? ' · ' : '';
                    splitLine.appendChild(document.createTextNode(MOVEMENT_LABELS[movementKey] + ' ' + seconds + 's' + separator));
                });
                roundListEl.appendChild(splitLine);
            }
        }
    }

    /**
     * Reveals `.cindy-finish-panel` in either variant (P-12's cap-reached auto-stop, or Task 2's
     * manual `.cindy-finish-early-btn` escape hatch) — `variant` is the ONE decision point for the
     * only structural difference the two share (UI-SPEC's own framing): whether
     * `.cindy-continue-btn` is visible. Idempotent and safe to call from either branch at any
     * time — if the cap fires while the manual variant is already open (an operator opened
     * "Finish session?" right as the clock reached 20:00), calling this again with `'cap'`
     * correctly re-hides Continue rather than leaving a stale manual panel showing an escape
     * hatch that no longer applies.
     */
    function showFinishPanel(variant: 'cap' | 'manual'): void {
        if (finishPanelTitleEl) {
            finishPanelTitleEl.textContent = variant === 'cap' ? 'Cap reached — 20:00' : 'Finish session?';
        }
        if (continueBtn) {
            continueBtn.classList.toggle('d-none', variant === 'cap');
        }
        if (finishPanelEl) finishPanelEl.classList.remove('d-none');
    }

    /** Dismisses the finish panel (Task 2's Continue button) without touching `currentState` —
     *  returning to the plain running view is a pure UI change, never a state change. */
    function hideFinishPanel(): void {
        if (finishPanelEl) finishPanelEl.classList.add('d-none');
    }

    /** Clears the repaint interval if one is running. RESEARCH.md Pitfall D: nothing to paint
     *  while hidden, so the interval is cleared entirely rather than left ticking into a
     *  background tab — restarted on the next visible repaint instead of throttled in place. */
    function stopRenderLoop(): void {
        if (renderIntervalId !== null) {
            clearInterval(renderIntervalId);
            renderIntervalId = null;
        }
    }

    /** Starts (or restarts) the repaint interval — clears any existing one first so a double-start
     *  can never stack two intervals. 500ms is chosen from RESEARCH.md Pitfall D's stated
     *  250-1000ms range: the display is whole-seconds by contract, so 500ms guarantees the shown
     *  second is never more than half a second stale, at a fraction of the DOM churn a 100ms
     *  hundredths-precision loop would cost. Does not paint immediately itself — every call site
     *  below calls `render()` once on its own right before this, so the corrected/initial number
     *  is on screen before the first tick. */
    function startRenderLoop(): void {
        stopRenderLoop();
        renderIntervalId = setInterval(render, 500); // 500ms — see the 250-1000ms range above
    }

    /**
     * WR-03: the ONE place this closure ever writes to storage — every other call site below
     * calls this instead of the imported `persistState()` directly. A no-op once
     * `isSupersededTab` is `true` (`lockTabAsSuperseded()` below), so a frozen tab can never
     * clobber whatever a second, active tab already wrote for the same session.
     */
    function persistIfActive(state: LiveTimerState): void {
        if (isSupersededTab) return;
        persistState(storage, state);
    }

    /**
     * WR-03: freezes this tab once a second `/workouts/live` tab has taken over the SAME
     * persisted session (`onStorageEvent` below). Idempotent (`isSupersededTab` itself is the
     * guard) — a second or third foreign write while already frozen is a silent no-op, not a
     * stacked re-application. Deliberately does NOT attempt to read or merge the foreign tab's
     * write: for a single-operator site, the only reachable failure mode is an accidental
     * duplicate tab, and merge/conflict resolution is explicitly out of scope for this fix —
     * converting a silent last-write-wins data loss into a visible, unmissable state is the
     * entire goal. Mirrors `applyCapUi()`'s own shape (stop the loop, disable every writer
     * control, reveal one clearly-labeled message) without touching `currentState` — a frozen
     * tab's in-memory state is simply abandoned, never persisted from this point on.
     */
    function lockTabAsSuperseded(): void {
        if (isSupersededTab) return;
        isSupersededTab = true;
        stopRenderLoop();
        if (tapBtn) {
            tapBtn.disabled = true;
            tapBtn.classList.add('d-none');
        }
        if (undoBtn) undoBtn.disabled = true;
        if (finishEarlyBtn) finishEarlyBtn.disabled = true;
        if (finishBtn) finishBtn.disabled = true;
        if (continueBtn) continueBtn.disabled = true;
        if (tabConflictWarningEl) tabConflictWarningEl.classList.remove('d-none');
    }

    /**
     * Best-effort screen Wake Lock request (RESEARCH.md Pattern 3). Feature-detects, requests, and
     * attaches a release listener that nulls the stored sentinel — a released sentinel is
     * unusable, so the only way back is calling `request()` again from a later trigger (the
     * visible branch of `onVisibility` below), never reusing this one. Every failure path
     * (unsupported, user declined, low-battery mode, no screen attached) is swallowed silently: no
     * log, no toast, no UI state anywhere reflects the outcome — REQUIREMENTS.md excludes Wake
     * Lock from the correctness contract outright, and P-10 is explicit this is convenience only.
     */
    async function requestWakeLockBestEffort(): Promise<void> {
        if (!('wakeLock' in navigator)) return;
        try {
            sentinel = await navigator.wakeLock.request('screen');
            // Per the navigator.wakeLock contract, the browser auto-releases this on ANY
            // visibility loss (screen lock, tab hide, app switch) — EXPECTED, not an error.
            sentinel.addEventListener('release', () => { sentinel = null; });
        } catch {
            // Fails silently: declined, low-battery mode, unsupported context, or no screen
            // attached — never surfaced to the operator (P-10, REQUIREMENTS.md's exclusion).
            sentinel = null;
        }
    }

    /** Start (pre-start -> running, P-05): reads the splits toggle at the moment of the click —
     *  never at mount, since the operator may flip it any number of times before starting — builds
     *  a fresh state via createInitialState()/startTimer(), persists it, paints the running screen
     *  once, reveals it, starts the repaint loop and opportunistically requests the Wake Lock.
     *  Resume takes no special path whatsoever: reconciling from a persisted `startInstant` is the
     *  same operation any other gap performs, and a "long gap" branch would be a second code path
     *  to keep correct for no benefit (P-09). */
    function onStartClick(): void {
        const splitsMode = splitsToggle ? splitsToggle.checked : false;
        currentState = startTimer(createInitialState(splitsMode), Date.now());
        persistIfActive(currentState); // WR-03: no-op once this tab is superseded (see persistIfActive() above)
        render();
        showState('running');
        startRenderLoop();
        void requestWakeLockBestEffort();
    }

    /** Resume (P-14): proceeds into the running state with the loaded state UNTOUCHED — no
     *  special-cased long-gap handling. Reconciling the display from the persisted `startInstant`
     *  is the same operation any other gap performs (P-09) — this is what keeps the resume prompt
     *  from ever showing a silent wrong number: the very first paint after Resume already reflects
     *  real elapsed time, not the static template's `00:00`/`20:00` defaults. */
    function onResumeClick(): void {
        render();
        showState('running');
        startRenderLoop();
        void requestWakeLockBestEffort();
    }

    /** Discard (P-14): clears the persisted blob and returns to a genuinely fresh pre-start screen
     *  — no confirmation dialog (the binary Resume/Discard choice screen IS the confirmation). */
    function onDiscardClick(): void {
        clearPersistedState(storage);
        currentState = createInitialState(false);
        if (splitsToggle) splitsToggle.checked = false;
        showState('pre-start');
    }

    /**
     * The single tap-target handler, in BOTH 1-tap and 3-tap modes (P-06). `tapInstant` is
     * captured as the very first statement — before any DOM read, `render()` call or await — and
     * is the ONLY instant `captureTap()` ever sees; PITFALLS.md lines 116-118 (item 5 of the
     * correct architecture) is explicit that a lap must be captured this way, never by reading
     * whatever `.cindy-timer-elapsed`/`.cindy-timer-remaining` currently show, which can be one
     * stale tick behind if the tab just resumed. All cycling/completion logic lives in
     * `captureTap()` (`live-timer-state.ts`) — this handler never reimplements it.
     *
     * A tap landing while `status !== 'running'` (a hidden or cap-stopped button) is a no-op, not
     * a late capture: `captureTap()` itself already returns the state unchanged in that case, but
     * this handler additionally skips persisting/repainting/re-arming the lockout so a stray click
     * on a non-running screen produces literally zero side effects.
     */
    function onTapClick(): void {
        const tapInstant = Date.now(); // PITFALLS.md item 5: the tap instant, never the displayed value
        if (currentState.status !== 'running') return;

        currentState = captureTap(currentState, tapInstant);
        persistIfActive(currentState); // WR-03: no-op once this tab is superseded (see persistIfActive() above)
        render();

        // Debounce lockout (T-264-17): the SAME window rejects a rapid double-tap outright rather
        // than queueing it — a queued second tap would turn one fat-finger double into two rounds,
        // exactly the corruption P-06's single-button design exists to minimise. `disabled` plus
        // the stylesheet's `pointer-events: none` (live.latte/style.scss, 264-06) makes the browser
        // drop the second event entirely; no buffering of any kind is added here.
        if (tapBtn) {
            tapBtn.disabled = true;
            tapLockoutId = setTimeout(() => {
                // WR-02: the `render()` call above may already have crossed the cap on THIS same
                // tap, setting `tapBtn.disabled = true` for good (its own cap branch). Guarding on
                // `status === 'running'` here keeps that permanent-disable intent intact — this
                // callback must never clear it back to enabled just because 450ms elapsed. `d-none`
                // already blocks a real click either way; this closes the state-consistency gap
                // for any future code path that ever checks `disabled` without also checking
                // `d-none`.
                if (tapBtn && currentState.status === 'running') tapBtn.disabled = false;
                tapLockoutId = null;
            }, 450); // 450ms — inside UI-SPEC's 400-600ms debounce-lockout range
        }
    }

    /**
     * Undo (P-08): drives entirely from `undoLast(state)` — no partial cleanup of its own, since
     * every boundary-instant restoration lives in that pure state-changing function
     * (`live-timer-state.ts`'s own docblock explains the derivation identity). Ignored when there
     * is nothing to undo (`describeUndoTarget(state).disabled`) or when `status !== 'running'`, so
     * a stale click after the cap or after finish can never mutate captured data. Raises no
     * confirm() dialog and no custom confirmation UI — matches 263's round-row-delete precedent
     * (nothing here has ever been saved yet).
     */
    function onUndoClick(): void {
        if (currentState.status !== 'running') return;
        if (describeUndoTarget(currentState).disabled) return;

        currentState = undoLast(currentState);
        persistIfActive(currentState); // WR-03: no-op once this tab is superseded (see persistIfActive() above)
        render();
    }

    /**
     * Manual finish (P-12's escape-hatch variant): reveals the finish panel with Continue visible.
     * Pauses nothing and stops nothing — the clock keeps running underneath, because a mis-tap
     * must cost the operator nothing — and leaves the primary tap target usable, so an accidental
     * tap that lands during the panel still captures a round rather than falling on the floor
     * (`onTapClick()` above is unaffected by the panel; its own `status === 'running'` guard is
     * the only gate it has ever needed). No-ops outside `'running'` so a stale click after the cap
     * (already showing its own variant, Continue permanently hidden) or after Finish can never
     * reopen or downgrade the panel.
     */
    function onFinishEarlyClick(): void {
        if (currentState.status !== 'running') return;
        showFinishPanel('manual');
    }

    /**
     * Continue (manual variant only): dismisses the panel and returns to the plain running view.
     * No confirm() dialog — the two-button panel IS the confirmation, matching this file's
     * established no-confirm-anywhere precedent (`onUndoClick()`'s own docblock above). The cap
     * variant never reveals this button in the first place (`showFinishPanel('cap')` keeps it
     * hidden), so this handler never has anything cap-related to guard against.
     */
    function onContinueClick(): void {
        hideFinishPanel();
    }

    /**
     * Finish — the ONE handler both panel variants share (P-12). Reads `#cindy-leftover-reps`
     * (an empty field reads as zero, never a rejection — `Number('')` is `0`, and the
     * live-timer-state coercion below already produces that). Funnels the cap path and the
     * manual path through the exact same underlying state change — there is no second, gentler
     * rule for a manual end; 263's P-08 stores no time-in-partial either way. Persists the
     * resulting `finished` state, clears the repaint interval, and hands off into the P-02 review
     * routine.
     */
    function onFinishBtnClick(): void {
        const leftoverRaw = leftoverInput ? leftoverInput.value : '';
        currentState = finishSession(currentState, Number(leftoverRaw));
        persistIfActive(currentState); // WR-03: no-op once this tab is superseded (see persistIfActive() above)
        stopRenderLoop();
        enterReviewState(currentState);
    }

    /**
     * P-02 hand-off (Task 3): the ONE place a captured/finished timer session becomes a pre-filled
     * Review & Save screen. Builds the exact `CindyEntrySession` shape the shared entry-form
     * mount already expects (264-03/264-04's locked contract), calls `.refresh()` on the SAME
     * controller `workouts.ts`'s own page-mount wiring set up at page load (`opts.entryController`
     * — never a second entry-form mount, never a second controller), tears down the running UI's
     * own continuous behaviour (repaint interval, debounce timeout, both reconciliation
     * listeners, Wake Lock release — T-264-20, `teardownRunningBehavior()` below does exactly
     * this and nothing more — the page's own Save button keeps working, so button click listeners
     * are deliberately left attached), and reveals the review screen.
     * `toLocalYmd(Date.now(), 'America/Toronto')` is the Toronto CALENDAR date, never re-converted
     * downstream (T-264-09 — this project's highest-priority standing constraint).
     *
     * Guards a null `entryController` (a template regression — the review mount point absent from
     * `live.latte`, unreachable in practice on this route) by returning without changing screens:
     * the state stays persisted and the operator stays on the finish panel, strictly safer than
     * losing a captured session to a missing mount point.
     */
    function enterReviewState(state: LiveTimerState): void {
        if (!opts.entryController) return;
        const session = toEntrySession(state, toLocalYmd(Date.now(), 'America/Toronto'));
        opts.entryController.refresh(session);
        teardownRunningBehavior();
        showState('review');
    }

    /**
     * The single shared reconciliation entry point (Task 2) — both the `visibilitychange` and
     * `pageshow` triggers below call this and nothing else, so there is no divergent logic
     * between the two paths for a bug to hide behind on whichever browser fires only one of them.
     */
    function recomputeAndRepaint(): void {
        render();
    }

    /** WR-01 discipline (`src/js/mc/console.ts:183-225`'s SHAPE, deliberately NOT its
     *  pause-and-do-nothing SEMANTICS: that feature stops work while hidden because it is fine
     *  losing time in between; this timer has no work to pause — `computeElapsed()` stays correct
     *  regardless of how long the page was hidden, so the only thing the visible branch does is
     *  recompute, repaint, and (Task 3) opportunistically re-request the Wake Lock. Named
     *  instance-scoped binding, not an inline closure, so `teardownListeners()` can remove it
     *  later. */
    const onVisibility = (): void => {
        if (document.hidden) {
            // Nothing to paint while hidden — clear the loop entirely rather than let it keep
            // ticking (and rendering) into a background tab (RESEARCH.md Pitfall D).
            stopRenderLoop();
            return;
        }
        // Recompute FIRST, then restart the loop, so the corrected number is already on screen
        // before the next tick — not up to 500ms stale.
        recomputeAndRepaint();
        if (currentState.status === 'running') startRenderLoop();
        if (!sentinel) void requestWakeLockBestEffort(); // sentinel is null after every release -- navigator.wakeLock must be re-requested, never reused
    };

    /** `event.persisted === true` marks a bfcache restore — a case `visibilitychange` does not
     *  reliably cover on every browser/navigation path (RESEARCH.md Pattern 2). Recomputing
     *  unconditionally here is idempotent, so the flag documents WHY this listener exists rather
     *  than gating on it. */
    const onPageShow = (_event: PageTransitionEvent): void => {
        recomputeAndRepaint();
    };

    /**
     * WR-03: fires on ANY write to `STORAGE_KEY` from another document — the `storage` event's
     * own contract is same-key-different-document only, so this tab's own `persistIfActive()`
     * calls never trigger it. Only acts while THIS tab's own in-memory session is still live
     * (`'running'` or `'cap-reached'`) — a write observed from the pre-start/resume/review
     * screens has nothing of this tab's own left to protect.
     */
    const onStorageEvent = (event: StorageEvent): void => {
        if (event.key !== STORAGE_KEY) return;
        if (currentState.status !== 'running' && currentState.status !== 'cap-reached') return;
        lockTabAsSuperseded();
    };

    if (startBtn) startBtn.addEventListener('click', onStartClick);
    if (resumeBtn) resumeBtn.addEventListener('click', onResumeClick);
    if (discardBtn) discardBtn.addEventListener('click', onDiscardClick);
    if (tapBtn) tapBtn.addEventListener('click', onTapClick);
    if (undoBtn) undoBtn.addEventListener('click', onUndoClick);
    if (finishEarlyBtn) finishEarlyBtn.addEventListener('click', onFinishEarlyClick);
    if (continueBtn) continueBtn.addEventListener('click', onContinueClick);
    if (finishBtn) finishBtn.addEventListener('click', onFinishBtnClick);

    // Both reconciliation triggers call the SAME recomputeAndRepaint() above — never divergent
    // logic. Deliberately NOT wired: the Page Lifecycle API's freeze/resume events, which have
    // zero support in Safari (any version, including iOS) or Firefox — on the operator's real
    // device those would never fire, and their presence here would read as false-confidence
    // coverage in a code review rather than the dead code it would actually be (RESEARCH.md
    // Pattern 2 / Pitfall A).
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('storage', onStorageEvent); // WR-03

    // ---- Mount-time routing (P-14, extended by Task 3 for P-02) — the ONE decision this module
    // makes without any tap: does a genuinely-abandoned persisted session exist, or a captured one
    // still awaiting Save? Never an automatic age-based clear (that decision belongs to the
    // operator, not a staleness threshold). A `'finished'` persisted state is neither resumable nor
    // a fresh start — it is a session Finish already captured but Save hasn't happened for yet (a
    // reload, or a discarded tab, between the two) — so it routes straight into the SAME review
    // hand-off a live Finish click uses, rather than either the resume prompt or a blank pre-start
    // screen. CR-01: a `'cap-reached'` persisted state is handled the SAME way — it is checked
    // BEFORE the generic `isResumable()` branch below (which would otherwise treat it identically
    // to a plain `'running'` session) because there is nothing left to resume from a wall-clock
    // perspective once the cap has already been reached: it routes straight into the running view
    // with the cap UI pre-applied via `applyCapUi()`, never the generic Resume/Discard prompt. ----
    const persisted = loadPersistedState(storage);
    if (persisted !== null && persisted.status === 'finished') {
        currentState = persisted;
        enterReviewState(currentState);
    } else if (persisted !== null && persisted.status === 'cap-reached') {
        // CR-01: previously fell into the generic isResumable() branch below, which showed the
        // P-14 Resume/Discard prompt — tapping Resume then called render() with currentState
        // already 'cap-reached', a status the ORIGINAL single-branch cap check never re-applied
        // the UI for, leaving a live-looking tap target that was a silent no-op with no way to
        // reach the finish panel or Save. No vibration fires on this path — that pulse belongs
        // only to the FIRST time this session's status became 'cap-reached', which already
        // happened on an earlier page load (possibly long before this one).
        currentState = persisted;
        render();
        showState('running');
        applyCapUi();
    } else if (persisted !== null && isResumable(persisted)) {
        currentState = persisted;
        if (resumeDateEl && persisted.startInstant !== null) {
            resumeDateEl.textContent = formatResumeDate(persisted.startInstant, 'America/Toronto');
        }
        showState('resume');
    } else {
        showState('pre-start');
    }

    // ---- Teardown ----

    /**
     * The running UI's own continuous behaviour, and NOTHING else (Task 3's original four-item
     * list, plus WR-03's `storage` listener on top): the repaint interval, the tap debounce
     * lockout, all three window/document-level listeners (`visibilitychange`, `pageshow`,
     * `storage`), and the Wake Lock. Deliberately does NOT remove any button's click listener — a
     * click listener bound to a now-hidden button (the pre-start/running containers are hidden by
     * `showState()`, never destroyed) is inert in real usage, since nothing can click a `d-none`
     * element. This is `enterReviewState()`'s own teardown call: the page stays very much alive
     * once the review screen is showing (the Save button's own listener, wired by `workouts.ts`,
     * keeps working), so a full listener strip would be the WRONG amount of cleanup here — only
     * the continuous background work (interval ticks, reconciliation, cross-tab detection) needs
     * to stop.
     */
    function teardownRunningBehavior(): void {
        stopRenderLoop(); // clearInterval() lives in stopRenderLoop() itself
        if (tapLockoutId !== null) {
            clearTimeout(tapLockoutId); // a navigation mid-lockout must never fire a stray re-enable
            tapLockoutId = null;
        }
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pageshow', onPageShow);
        window.removeEventListener('storage', onStorageEvent); // WR-03
        if (sentinel) {
            void sentinel.release(); // release any held navigator.wakeLock sentinel on teardown
            sentinel = null;
        }
    }

    /** Named handler bindings (not anonymous closures) so removeEventListener can find them
     *  later, the same WR-01 discipline console.ts's visibilitychange wiring uses. A full
     *  page-level unmount: every click listener PLUS `teardownRunningBehavior()`'s own four items.
     *  Not invoked by anything today — this page is never dynamically unmounted, matching every
     *  other single-page module in this codebase — but it exists now so later plans have one
     *  place to extend, not two. */
    function teardownListeners(): void {
        if (startBtn) startBtn.removeEventListener('click', onStartClick);
        if (resumeBtn) resumeBtn.removeEventListener('click', onResumeClick);
        if (discardBtn) discardBtn.removeEventListener('click', onDiscardClick);
        if (tapBtn) tapBtn.removeEventListener('click', onTapClick);
        if (undoBtn) undoBtn.removeEventListener('click', onUndoClick);
        if (finishEarlyBtn) finishEarlyBtn.removeEventListener('click', onFinishEarlyClick);
        if (continueBtn) continueBtn.removeEventListener('click', onContinueClick);
        if (finishBtn) finishBtn.removeEventListener('click', onFinishBtnClick);
        teardownRunningBehavior();
    }
    // teardownListeners() is available for any later
    // plan to extend further.
}
